import React from 'react'
import { motion, Variants } from 'framer-motion'
import mobilefeatureImage from '../../../assets/images/ddxpage/featuremobile.webp'
import buildImage from '../../../assets/images/ddxpage/whatwebuild.webp'
import featureImage from '../../../assets/images/ddxpage/feature.webp'

const buildPointsLeft = [
  'AI-powered symptom analysis engine',
  'Differential diagnosis system with intelligent predictions',
  'Conversational chat- based user experience',
]

const buildPointsRight = [
  'Remote care & consultation integration',
  'Real-time health insights & smart recommendations',
  `Patient history tracking with secure cloud infrastructure`,
]

const features = [
  'AI-powered symptom analysis',
  'Differential diagnosis system',
  'Smart recommendations & next steps',
  'Patient history tracking',
  'Remote consultation support',
  'Secure, compliant data architecture',
]

const leftVariant: Variants = {
  hidden: {
    opacity: 0,
    x: -120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  },
}

const rightVariant: Variants = {
  hidden: {
    opacity: 0,
    x: 120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  },
}

const outcomeVariant: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.7,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: 'easeOut',
    },
  },
}

const WhatWeBuild = () => {
  return (
    <section className="w-full bg-[#F3EEF8]">
      {/* WHAT WE BUILD */}
      <div className="relative mx-auto w-full overflow-hidden px-5 py-14 md:px-10 lg:px-16">
        {/* LEFT GLOW */}
        <div
          className="pointer-events-none absolute left-[50px] top-1/2 h-[420px] w-[100px] -translate-y-1/2 rotate-12 rounded-full blur-[140px] opacity-80 xl:left-[150px] 2xl:left-[300px]"
          style={{
            background:
              'linear-gradient(293.91deg,#2B0946 -61.94%,#5E0F9B 100%)',
          }}
        />

        {/* RIGHT GLOW */}
        <div
          className="pointer-events-none absolute right-[50px] top-1/2 h-[420px] w-[100px] -translate-y-1/2 -rotate-12 rounded-full blur-[140px] opacity-80 xl:right-[150px] 2xl:right-[300px]"
          style={{
            background:
              'linear-gradient(293.91deg,#2B0946 -61.94%,#5E0F9B 100%)',
          }}
        />

        <div className="relative z-10">
          {/* HEADING */}
          <h2
            className="bg-clip-text text-center font-gilroy-extrabold text-4xl text-transparent md:text-4xl 2xl:text-6xl"
            style={{
              background:
                'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
              WebkitBackgroundClip: 'text',
            }}
          >
            WHAT WE BUILT
          </h2>

          {/* MAIN */}
          <div className="mx-auto mt-7 flex max-w-[1900px] flex-col items-center justify-center gap-10 text-center md:mt-16 lg:flex-row">
            {/* LEFT */}
            <div className="flex flex-1 flex-col gap-3 md:gap-12">
              {buildPointsLeft.map((item, index) => (
                <motion.div
                  key={item}
                  variants={leftVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{ delay: index * 0.15 }}
                  className="flex items-start gap-3 md:gap-5"
                >
                  <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#7A2CB8] md:h-7 md:w-7">
                    <div
                      className="h-2 w-2 rounded-full md:h-4 md:w-4"
                      style={{
                        background:
                          'linear-gradient(294.77deg,#2B0946 -6.05%,#5E0F9B 100%)',
                      }}
                    />
                  </div>

                  <p className="text-left font-gilroy-medium text-lg leading-[1.4] text-[#0A0111] md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl">
                    {item}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* CENTER */}
            <div className="flex justify-center">
              <div
                className="rounded-t-[220px] p-[2px] md:p-[3px]"
                style={{
                  background:
                    'linear-gradient(147.21deg,#5E0F9B 13.21%,#2B0946 114.4%)',
                  boxShadow: '0px 0px 40px rgba(75, 13, 123, 0.459)',
                }}
              >
                <div className="flex w-[270px] items-end justify-center rounded-t-[230px] bg-[#F3EEF8] px-8 pt-10 md:w-[420px] lg:w-[300px] xl:w-[420px] 2xl:w-[430px]">
                  <motion.img
                    src={buildImage}
                    alt=""
                    animate={{
                      y: [0, -22, 0],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'easeIn',
                    }}
                    className="h-[300px] object-contain py-10 md:h-[420px] lg:h-[400px] xl:h-[480px] 2xl:h-[550px]"
                  />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex flex-1 flex-col items-start gap-3 md:gap-12">
              {buildPointsRight.map((item, index) => (
                <motion.div
                  key={item}
                  variants={rightVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{ delay: index * 0.15 }}
                  className="flex items-start gap-3 md:gap-5 lg:flex-row-reverse"
                >
                  <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#7A2CB8] md:h-7 md:w-7">
                    <div
                      className="h-2 w-2 rounded-full md:h-4 md:w-4"
                      style={{
                        background:
                          'linear-gradient(294.77deg,#2B0946 -6.05%,#5E0F9B 100%)',
                      }}
                    />
                  </div>

                  <p className="text-left font-gilroy-medium text-lg leading-[1.4] text-[#0A0111] md:text-lg lg:text-right lg:text-xl xl:text-2xl 2xl:text-3xl">
                    {item}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* OUTCOME */}
      <motion.div
        variants={outcomeVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        className="px-5 lg:py-16"
      >
        <div className="flex justify-center">
          <div className="flex items-center gap-5">
            <div className="h-3 w-3 rounded-full bg-[#0A0111] md:h-4 md:w-4" />

            <h2
              className="bg-clip-text font-gilroy-extrabold text-3xl uppercase text-transparent md:text-4xl 2xl:text-6xl"
              style={{
                background:
                  'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
                WebkitBackgroundClip: 'text',
              }}
            >
              Outcome
            </h2>

            <div className="h-3 w-3 rounded-full bg-[#0A0111] md:h-4 md:w-4" />
          </div>
        </div>

        <h3 className="lg:mt-8 2xl:mt-12 text-center font-gilroy-bold text-xl text-[#0A0111] md:text-3xl 2xl:text-4xl">
          From uncertainty to clarity—in seconds.
        </h3>

        <p className="mx-auto mt-4 max-w-[1100px] text-center font-gilroy-medium text-lg leading-relaxed text-[#0A0111] 2xl:mt-8 2xl:text-3xl">
          DDX enables faster initial diagnosis, improves user confidence,
          and expands access to early-stage healthcare. The result is a
          more informed, proactive, and connected care experience.
        </p>
      </motion.div>

     {/* FEATURE */}
<div className="relative mx-auto w-full overflow-hidden">

  {/* MOBILE HEADING */}
  <div className="px-5 pt-10 text-center md:hidden">
    <div className="-mb-10 flex items-center justify-center gap-5 md:mb-6">

      <h2
        className="bg-clip-text font-gilroy-extrabold uppercase text-3xl text-transparent"
        style={{
          background:
            'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
          WebkitBackgroundClip: 'text',
        }}
      >
        Feature
      </h2>

      <div className="h-3 w-3 rounded-full bg-[#0A0111]" />

    </div>
  </div>

  {/* DESKTOP IMAGE */}
  <img
    src={featureImage}
    alt=""
    className="hidden h-[320px] w-full object-cover md:block md:h-[500px] lg:h-[550px] xl:h-[800px] 2xl:h-[1400px]"
  />

  {/* MOBILE IMAGE */}
  <div className="flex justify-center pt-16 md:hidden">
    <img
      src={mobilefeatureImage}
      alt=""
      className="w-full max-w-[380px] object-contain"
    />
  </div>

  {/* MOBILE POINTS */}
  <div className="grid gap-2 px-5 py-8 text-center md:hidden">

    {features.map((item) => (
      <div
        key={item}
        className="rounded-[20px] px-4 py-3"
        style={{
          background: 'rgba(255,255,255,.75)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <p className="font-gilroy-medium text-base text-[#0A0111]">
          {item}
        </p>
      </div>
    ))}

  </div>

  {/* DESKTOP OVERLAY */}
  <div className="absolute right-[5%] top-1/2 z-20 hidden w-[92%] max-w-[950px] -translate-y-1/2 md:block md:w-[40%]">

    <div className="mb-4 flex items-center gap-5 lg:mb-6 2xl:mb-12">

      <h2
        className="bg-clip-text font-gilroy-extrabold uppercase text-3xl text-transparent md:text-3xl lg:text-4xl 2xl:text-6xl"
        style={{
          background:
            'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
          WebkitBackgroundClip: 'text',
        }}
      >
        Feature
      </h2>

      <div className="h-4 w-4 rounded-full bg-[#0A0111]" />

    </div>

    <div className="grid md:gap-3 lg:gap-5 2xl:gap-7">

      {features.map((item, index) => (
        <motion.div
          key={item}
          initial={{
            opacity: 0,
            x: 80,
          }}
          whileInView={{
            opacity: 1,
            x: 0,
          }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{
            duration: 0.7,
            delay: index * 0.08,
          }}
          className="rounded-[20px] px-3 py-3 xl:px-6 xl:py-5"
          style={{
            background: 'rgba(255,255,255,.75)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
        >
          <p className="font-gilroy-medium text-base text-[#0A0111] md:text-base lg:text-xl xl:text-2xl 2xl:text-3xl">
            {item}
          </p>
        </motion.div>
      ))}

    </div>

  </div>

</div>
    </section>
  )
}

export default WhatWeBuild